// Segment: genau eine von zwei oder drei gleichwertigen Arten, als zusammenhaengende
// Pille (v4 .seg; ST-UX-001). Fuer eine Auswahl, die das Formular darunter umbaut
// ("Art des Kontakts"); Filter bleiben Schaltergruppe.
//
// Semantik: Umschaltknoepfe (Fluent ToggleButton, aria-pressed) in einer benannten
// Gruppe, jeder mit eigenem Tab-Halt, Enter und Leertaste schalten (Entscheidung in
// ST-UX-001). Genau einer ist gedrueckt; der gedrueckte loest sich nicht durch
// erneutes Druecken. Gedrueckt traegt Markenflaeche, Rahmen und halbfette Schrift,
// der Wechsel ist also nicht nur Farbe (WCAG 1.4.1).

import type { ReactElement } from 'react';
import { makeStyles, mergeClasses, ToggleButton, tokens } from '@fluentui/react-components';

export interface SegmentOption {
  readonly id: string;
  readonly label: string;
}

const useStyles = makeStyles({
  gruppe: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    flexWrap: 'wrap',
    gap: '0.125rem',
    // Innenabstand statt overflow: hidden, damit der Fokusring nicht abgeschnitten wird.
    padding: '0.125rem',
    // Feldgrenze >= 3:1 gegen die Karte (WCAG 1.4.11).
    border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
    borderRadius: '999px',
  },
  knopf: {
    minWidth: 0,
    minHeight: '2rem',
    padding: '0 0.875rem',
    fontWeight: tokens.fontWeightRegular,
    // Fingerziel 44 px, wo mit dem Finger bedient wird (Muster mobil 44 px).
    '@media (pointer: coarse)': { minHeight: '2.75rem' },
  },
  gedrueckt: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': { backgroundColor: tokens.colorBrandBackground2Hover, color: tokens.colorBrandForeground2Hover },
    ':hover:active': { backgroundColor: tokens.colorBrandBackground2Pressed, color: tokens.colorBrandForeground2Pressed },
  },
});

export function Segment({
  label,
  optionen,
  gewaehlt,
  onWahl,
}: {
  /** Name der Gruppe (aria-label), z. B. "Art des Kontakts". */
  readonly label: string;
  readonly optionen: readonly SegmentOption[];
  readonly gewaehlt: string;
  readonly onWahl: (id: string) => void;
}): ReactElement {
  const own = useStyles();
  return (
    <div role="group" aria-label={label} className={own.gruppe}>
      {optionen.map((option) => {
        const an = option.id === gewaehlt;
        return (
          <ToggleButton
            key={option.id}
            shape="circular"
            appearance="subtle"
            checked={an}
            data-segment={option.id}
            className={mergeClasses(own.knopf, an && own.gedrueckt)}
            onClick={() => {
              if (!an) onWahl(option.id);
            }}
          >
            {option.label}
          </ToggleButton>
        );
      })}
    </div>
  );
}
